import React, { useState, useEffect, useRef, useCallback } from 'react';
import { DndContext, closestCorners, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { useNavigate } from 'react-router-dom';
import TaskPane from './TaskPane';
import Grid from './Grid';
import { DELETE_API, GET_API, POST_API, PUT_API } from '../api';

const API_URL = `${process.env.REACT_APP_API_URL}/v1` || 'http://localhost:8001';

const Main = () => {
    const navigate = useNavigate();
    const [isAdding, setIsAdding] = useState(false)
    const [values, setValues] = useState({
        name: '',
    })
    const [paneTasks, setPaneTasks] = useState([])
    const [gridTasks, setGridTasks] = useState([])
    const [completedTasks, setCompletedTasks] = useState([])

    const textareaRef = useRef(null);

    const effortLevels = ['High', 'Medium', 'Low']
    const impactLevels = ['Low', 'Medium', 'High']

    useEffect(() => {
        fetchPaneTasks();
        fetchGridState();
    }, []);

    const fetchPaneTasks = async () => {
        try {
            const response = await GET_API('tasks/pane-tasks');
            setPaneTasks(response);
        } catch (error) {
            console.error('Failed to fetch pane tasks:', error);
        }
    };

    const fetchCompletedTasks = useCallback(async () => {
        try {
            const response = await GET_API('tasks/completed-tasks');
            setCompletedTasks(response);
        } catch (error) {
            console.error('Error fetching completed tasks:', error);
            setCompletedTasks([]);
        }
    }, [])

    const fetchGridState = async () => {
        try {
            const response = await GET_API(`tasks/grid-tasks`);
            console.log('Grid tasks response:', response);
            setGridTasks(response);
        } catch (error) {
            console.error('Failed to fetch grid state:', error);
        }
    };

    const handleChange = (e) => {
        setValues({
            ...values,
            [e.target.name]: e.target.value
        })
    }

    const handleDragEnd = async (event) => {
        const { active, over } = event;

        if (!over) return;

        const draggedTaskId = active.id;
        const destinationId = over.id;

        let draggedTask = paneTasks.find(task => task.task_id === draggedTaskId);
        let isFromPane = true;

        if (!draggedTask) {
            draggedTask = gridTasks.find(task => task.task_id === draggedTaskId);
            isFromPane = false;
        }

        if (!draggedTask) {
            console.error('Dragged item not found');
            return;
        }

        try {
            if (destinationId === 'task-pane') {
                // Moving to pane
                if (!isFromPane) {
                    await POST_API(`tasks/pane-tasks/`, {
                        task: draggedTask.task,
                        index: paneTasks.length
                    });
                    await DELETE_API(`tasks/grid-tasks/${draggedTaskId}`);
                }
            } else {
                // Moving to grid
                const [priority, impact] = destinationId.split('_');
                if (isFromPane) {
                    await POST_API(`tasks/grid-tasks/`, {
                        task: draggedTask.task,
                        priority: priority,
                        impact: impact
                    });
                    await DELETE_API(`tasks/pane-tasks/${draggedTaskId}`);
                } else {
                    await PUT_API(`tasks/grid-tasks/${draggedTaskId}`, {
                        task: draggedTask.task,
                        priority: priority,
                        impact: impact
                    });
                }
            }

            // Fetch the updated states
            await fetchPaneTasks();
            await fetchGridState();
        } catch (error) {
            console.error('Failed to update task:', error);
        }
    }

    const handleSubmit = (e) => {
        e.preventDefault()
        createTask()
    }

    const createTask = async () => {
        if (values.name.trim() === '') return;
        let payload = {
            task: values.name,
            index: paneTasks.length
        }
        try {
            await POST_API(`tasks/pane-tasks/`, payload);
            setValues({ name: '' });
            setIsAdding(false);
            await fetchPaneTasks();
        } catch (error) {
            console.error('Failed to create task:', error);
        }
    }

    const toggleAdding = () => {
        setIsAdding(!isAdding)
        if (!isAdding) {
            setTimeout(() => {
                textareaRef.current?.focus()
            }, 0)
        }
    }

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            if (e.shiftKey) {
                // Shift+Enter: Create a new line below the existing line
                return; // Default behavior will create a new line
            } else {
                // Enter: Create the task
                e.preventDefault();
                createTask();
            }
        }
        if (e.metaKey && e.shiftKey && e.key === 'S') {
            e.preventDefault();
            toggleAdding();
        }
    }

    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown)
        return () => window.removeEventListener('keydown', handleKeyDown)
    }, [])

    const handleDelete = async (taskId, taskType) => {
        try {
            if (taskType === 'pane') {
                await DELETE_API(`tasks/pane-tasks/${taskId}`);
                await fetchPaneTasks();
            } else if (taskType === 'grid') {
                await DELETE_API(`tasks/grid-tasks/${taskId}`);
                await fetchGridState();
            } else if (taskType === 'completed') {
                await DELETE_API(`tasks/completed-tasks/${taskId}`);
                await fetchCompletedTasks();
            }
        } catch (error) {
            console.error('Failed to delete task:', error);
        }
    }

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor)
    );

    const handleComplete = async (taskId) => {
        try {
            await PUT_API(`tasks/${taskId}/complete`, { completed: true });
            await fetchGridState();
        } catch (error) {
            console.error('Failed to complete task:', error);
        }
    }

    const handleLogout = () => {
        // Implement logout logic here
        // For example, clear local storage, reset state, etc.
        localStorage.removeItem('token'); // Assuming you store the auth token in localStorage
        navigate('/'); // Redirect to login page
    };

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCorners}
            onDragEnd={handleDragEnd}
        >
            <div className='flex flex-col h-screen p-4 font-poppins'>
                <div className='flex justify-end mb-4'>
                    <button
                        onClick={handleLogout}
                        className='bg-red-500 hover:bg-red-600 text-white font-bold py-2 px-4 rounded'
                    >
                        Logout
                    </button>
                </div>
                <div className='flex flex-1'>
                    <div className='w-24 flex items-center justify-center'>
                        <span className='transform -rotate-90 text-xl font-bold'>Effort</span>
                    </div>
                    <Grid
                        effortLevels={effortLevels}
                        impactLevels={impactLevels}
                        gridTasks={gridTasks}
                        handleDelete={handleDelete}
                        handleComplete={handleComplete}
                    />
                    <TaskPane
                        tasks={paneTasks}
                        setTasks={setPaneTasks}
                        isAdding={isAdding}
                        values={values}
                        handleChange={handleChange}
                        handleSubmit={handleSubmit}
                        toggleAdding={toggleAdding}
                        textareaRef={textareaRef}
                        handleKeyDown={handleKeyDown}
                        handleDelete={handleDelete}
                        setIsLoading={() => false}
                        handleComplete={handleComplete}
                        fetchCompletedTasks={fetchCompletedTasks} //TODO: Need to refactor this
                        completedTasks={completedTasks}
                        setCompletedTasks={setCompletedTasks}
                    />
                </div>
            </div>
        </DndContext>
    )
}

export default Main;